import { create } from "zustand";
import offerService from "../services/offerService";

const useOfferStore = create((set, get) => ({
  // State
  offers: [],
  loading: false,
  error: null,
  selectedOffer: null,
  selectedApplication: null,
  filters: {
    status: "",
    search: "",
  },
  pagination: {
    page: 1,
    limit: 10,
    totalDocs: 0,
    totalPages: 1,
    hasPrevPage: false,
    hasNextPage: false,
    prevPage: null,
    nextPage: null,
  },

  // Actions
  setFilters: (filters) => set({ filters }),
  setSelectedOffer: (offer) => set({ selectedOffer: offer }),
  setSelectedApplication: (app) => set({ selectedApplication: app }),
  setPage: (page) =>
    set((state) => ({
      pagination: { ...state.pagination, page },
    })),
  setPagination: (paginationData) =>
    set((state) => ({
      pagination: { ...state.pagination, ...paginationData },
    })),

  // Async Actions
  fetchOffers: async () => {
    try {
      const { filters, pagination } = get();
      set({ loading: true, error: null });
      const response = await offerService.getAllOfferData({
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
      });

      // Handle nested data structure
      const {
        data: { data: offers, ...paginationData },
      } = response;

      // Transform offers data to match the display requirements
      const transformedOffers = offers.map((offer) => {
        // Handle the different ID formats from backend
        const id = offer._id || offer.id;

        // Get candidate name with fallbacks
        let candidateName = "No Candidate";
        if (offer.name) {
          candidateName = offer.name;
        } else if (
          offer.candidateName &&
          offer.candidateName !== "Not Available"
        ) {
          candidateName = offer.candidateName;
        } else if (offer.candidateInfo?.name) {
          candidateName = offer.candidateInfo.name;
        }

        return {
          _id: id,
          ...offer,
          candidateName:
            offer.name ||
            (offer.candidateName && offer.candidateName !== "Not Available"
              ? offer.candidateName
              : null) ||
            offer.candidateInfo?.name ||
            offer.candidate?.fullName ||
            (offer.candidate?.firstName && offer.candidate?.lastName
              ? `${offer.candidate.firstName} ${offer.candidate.lastName}`
              : null) ||
            "No Candidate",

          // Normalize the position and department
          position:
            offer.position ||
            (offer.requisitionId
              ? offer.requisitionId.jobTitle ||
                offer.requisitionId.positionTitle ||
                "Position Not Specified"
              : "Position Not Specified"),

          department:
            offer.department ||
            offer.requisitionId?.department ||
            "No Department",

          // Ensure these fields are always present
          status: offer.status || offer.displayStatus || "draft",
          offerDate:
            offer.offerDate || offer.createdAt || new Date().toISOString(),
          validTill: offer.validTill,
          documentUrl: offer.documentUrl,
          hasOffer: true, // Since these are actual offers
        };
      });

      set({
        offers: transformedOffers,
        pagination: {
          ...get().pagination,
          ...paginationData,
          page: parseInt(paginationData.page),
          limit: parseInt(paginationData.limit),
          totalDocs: parseInt(paginationData.totalDocs),
          totalPages: parseInt(paginationData.totalPages),
        },
        loading: false,
      });
    } catch (error) {
      set({
        loading: false,
        error: error.message || "Failed to fetch offers",
      });
      console.error("Error fetching offers:", error);
    }
  },

  sendOffer: async (offerId) => {
    try {
      await offerService.sendOffer(offerId);
      get().fetchOffers(); // Refresh the offers list
      return { success: true };
    } catch (error) {
      console.error("Error sending offer:", error);
      return {
        success: false,
        error: error.message || "Failed to send offer",
      };
    }
  },

  withdrawOffer: async (offerId, reason) => {
    try {
      await offerService.withdrawOffer(offerId, reason);
      get().fetchOffers(); // Refresh the offers list
      return { success: true };
    } catch (error) {
      console.error("Error withdrawing offer:", error);
      return {
        success: false,
        error: error.message || "Failed to withdraw offer",
      };
    }
  },

  // Reset state
  resetState: () =>
    set({
      offers: [],
      loading: false,
      error: null,
      selectedOffer: null,
      selectedApplication: null,
      filters: {
        status: "",
        search: "",
      },
    }),
}));

export default useOfferStore;
